import { ChatGPTCompletionResponse } from '@/models/ai.models';
import { AiModule } from '@/modules/ai.module';
import { organizationData } from '@/store/helpers';

export const inferAnything = async <
  L1 extends number | string = string,
  V1 extends number | string = string,
  L2 extends number | string = string,
  V2 extends number | string = string
>(opts: {
  entity1: Array<{ label: L1; value: V1 }>;
  entity2: Array<{ label: L2; value: V2 }>;
  entity1Name?: string;
  entity2Name?: string;
}): Promise<
  Array<{ e1: { label: L1; value: V1 }; e2: { label: L2; value: V2 } }>
> => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');
  const aiModule = new AiModule(organization.value.id);

  const systemPrompt = `
    - You will be provided with two lists of labels. Groups might be named to provide additional context.
    - Please select the best match between the two groups.
    - Try to match as many as possible by using loose associations, name similarities, or any other kind of similarity.
    - Multiple matches are allowed. Don't include labels that you can't find a match for.

    Return type: Array<[labelFromGroup1, labelFromGroup2]>; in JSON; no extraneous text.

    Example:

    Group 1:
      Name: Options
      Values: ["Bug", "Frontend", "Backend", "New Feature", "AI/ML"]

    Group 2:
      Name: Options
      Values: ["UI/UX", "Ops", "Issue", "Story"]

    Your response should be:
      [["Bug", "Issue"], ["Frontend", "UI/UX"], ["Backend", "Ops"], ["New Feature", "Story"]]
  `;

  const userPrompt = `
    Group 1:
      Name: ${opts.entity1Name || 'No name provided'}
      Values: [${opts.entity1.map((e) => `"${e.label}"`).join(', ')}]

    Group 2:
      Name: ${opts.entity2Name || 'No name provided'}
      Values: [${opts.entity2.map((e) => `"${e.label}"`).join(', ')}]
  `;

  const response = await aiModule.proxy<ChatGPTCompletionResponse>(
    'chat/completions',
    {
      method: 'POST',
      body: {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: systemPrompt,
          },
          {
            role: 'user',
            content: userPrompt,
          },
        ],
      },
    }
  );

  const extractedArray = (() => {
    const content = response.output?.choices[0].message.content || '';
    // extract the JSON object from the response content
    const jsonStart = content.indexOf('[');
    const jsonEnd = content.lastIndexOf(']');
    if (jsonStart === -1 || jsonEnd === -1) return [];
    const str = content.substring(jsonStart, jsonEnd + 1);
    try {
      const arr = JSON.parse(str) as Array<unknown>;
      const ret: Array<[L1, L2]> = [];
      for (const val of arr) {
        if (
          Array.isArray(val) &&
          val.length === 2 &&
          typeof val[0] === 'string' &&
          typeof val[1] === 'string'
        ) {
          ret.push(val as [L1, L2]);
        }
      }
      return ret;
    } catch (e) {
      return [];
    }
  })();

  // match the extracted array to the original entities

  const next: Array<{
    e1: { label: L1; value: V1 };
    e2: { label: L2; value: V2 };
  }> = [];
  for (const [label1, label2] of extractedArray) {
    const e1 = opts.entity1.find((e) => e.label === label1);
    const e2 = opts.entity2.find((e) => e.label === label2);
    if (e1 && e2) next.push({ e1, e2 });
  }

  return next;
};
